import React from "react";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, Sparkles } from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";
import UNSPLASH from "../../unsplashImages";

const EXAMPLE_OFFERINGS = [
  {
    title: "Sunset Catamaran Charter",
    price: "$220",
    tag: "Experience",
    img: "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=900&h=600&fit=crop&q=80",
  },
  {
    title: "Beach Cruiser Rentals",
    price: "$45 / day",
    tag: "Rental",
    img: "https://images.unsplash.com/photo-1532298229144-0ec0c57515c7?w=900&h=600&fit=crop&q=80",
  },
  {
    title: "Arrival Champagne Setup",
    price: "$85",
    tag: "Signature",
    img: UNSPLASH.services.arrivalEvent,
  },
];

export default function StageMarketplace({
  onCreate,
  customServices = [],
  hostLogo,
  previewToggles,
}) {
  const enabledServices = React.useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(previewToggles || {}).forEach(([k, on]) => {
      if (on) set.add(k.replace(/_/g, "-"));
    });
    return set;
  }, [previewToggles]);

  return (
    <div className="relative w-full h-full grid grid-rows-[minmax(0,38%)_minmax(0,62%)] laptop:grid-rows-1 laptop:grid-cols-[1.15fr_0.85fr]">
      {/* Left — aspirational pitch */}
      <div className="relative px-6 laptop:px-14 py-3 laptop:py-6 order-2 laptop:order-1 min-h-0 flex flex-col overflow-y-auto laptop:overflow-hidden">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1.5">
            Step 4 · Your Marketplace
          </p>
          <h2 className="text-[22px] laptop:text-[30px] font-extrabold text-[#111111] leading-[1.05]" style={{ letterSpacing: -0.5 }}>
            Your properties. Your offerings.
            <br />
            <span className="text-[#EF4949]">Your margins.</span>
          </h2>
          <p className="mt-3 text-[13px] laptop:text-[14px] text-[#5F6368] max-w-[520px] leading-relaxed">
            Add anything guests will love — rentals, experiences, welcome
            packages, add-ons. You keep 100% of what you charge on top of the
            platform fee.
          </p>
        </div>

        {/* Hero example + mini examples */}
        <div className="mt-4 laptop:mt-6 flex-1 min-h-0 grid grid-rows-[minmax(0,1.4fr)_auto] gap-3">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-[20px] border border-[#EAEAEA] shadow-[0_18px_60px_rgba(0,0,0,0.08)]"
          >
            <img
              src={EXAMPLE_OFFERINGS[0].img}
              alt={EXAMPLE_OFFERINGS[0].title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 50%, transparent 100%)",
              }}
            />
            <div className="relative h-full min-h-[140px] laptop:min-h-[180px] flex flex-col justify-end p-4 laptop:p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur text-[9px] font-bold uppercase tracking-[0.18em] text-white border border-white/25">
                  Your brand
                </span>
                <span className="text-[10px] font-semibold text-white/80 uppercase tracking-[0.18em]">
                  {EXAMPLE_OFFERINGS[0].tag}
                </span>
              </div>
              <div className="flex items-end justify-between gap-3">
                <div>
                  <h3 className="text-[17px] laptop:text-[20px] font-extrabold text-white leading-tight" style={{ letterSpacing: -0.3 }}>
                    {EXAMPLE_OFFERINGS[0].title}
                  </h3>
                  <p className="text-[11px] text-white/70 mt-1 hidden laptop:block">
                    An example of what your marketplace can offer.
                  </p>
                </div>
                <span className="text-[18px] laptop:text-[22px] font-extrabold text-white tabular-nums" style={{ letterSpacing: -0.3 }}>
                  {EXAMPLE_OFFERINGS[0].price}
                </span>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-2 gap-3">
            {EXAMPLE_OFFERINGS.slice(1).map((o, i) => (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 + i * 0.05 }}
                className="relative overflow-hidden rounded-[14px] border border-[#EAEAEA]"
                style={{ aspectRatio: "16 / 10" }}
              >
                <img src={o.img} alt={o.title} className="absolute inset-0 w-full h-full object-cover" />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.1) 55%, transparent 100%)",
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/80">{o.tag}</span>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12px] laptop:text-[13px] font-bold text-white truncate">{o.title}</span>
                    <span className="text-[11px] font-bold text-white/90 tabular-nums flex-shrink-0">{o.price}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={onCreate}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[12px] bg-[#EF4949] text-white text-[14px] font-bold hover:bg-[#DC2626] transition-colors shadow-[0_8px_32px_rgba(239,73,73,0.14)]"
          >
            <Plus size={15} strokeWidth={2.5} />
            Add your first offering
          </button>
          {customServices.length > 0 && (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#10B981]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              {customServices.length} draft{customServices.length === 1 ? "" : "s"} ready to go live
            </span>
          )}
        </div>
      </div>

      {/* Right — phone */}
      <div className="relative order-1 laptop:order-2 min-h-0 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 50% 40%, rgba(239,73,73,0.08), transparent 60%)",
          }}
        />
        <div className="relative h-full flex items-center justify-center py-2 laptop:py-6">
          <PhoneAnchor stage={4} enabledServices={enabledServices} hostLogo={hostLogo} />
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="hidden laptop:flex absolute top-6 right-6 items-center gap-2 px-3 py-2 rounded-full bg-white border border-[#EAEAEA] shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
        >
          <Sparkles size={12} className="text-[#EF4949]" />
          <span className="text-[10px] font-semibold text-[#111111]">Your branded guest storefront</span>
          <ArrowUpRight size={11} className="text-[#888888]" />
        </motion.div>
      </div>
    </div>
  );
}
